'use client';

import { InviteHero, ProductSection } from 'components/invite';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  clearUltraworkSession,
  readUltraworkSession,
  verifyUltraworkToken,
  writeUltraworkSession,
  type UltraworkMagicLinkResponse,
} from 'utils/affiliateHelpers';
import { Hero } from 'components/affiliate-work/partner/Hero';
import { ProductCardSection } from 'components/affiliate-work/partner/ProductCardSection';
import { SessionProvider } from 'next-auth/react';
import { PartnerElementData } from 'utils/affiliateHelpers';

// Survives a Strict Mode remount, so the single-use magic link is verified
// once even when the effect body runs twice in development.
const verifyInflight = new Map<string, Promise<UltraworkMagicLinkResponse>>();

function verifyOnce(
  token: string,
  email: string,
  companyCode: string,
): Promise<UltraworkMagicLinkResponse> {
  const existing = verifyInflight.get(token);
  if (existing) return existing;
  const pending = verifyUltraworkToken(token, email, companyCode);
  verifyInflight.set(token, pending);
  return pending;
}

export type InviteAffiliateType = 'work' | 'indi';

function Affiliate({ data }: { data?: any }) {
  const {
    data: { code },
  } = data;

  useEffect(() => {
    // affiliate code in the URL without re-running the server page. Everything
    // else already in the query (utm_*, discount) is preserved; the `invite`
    // route param is not part of the query string under the App Router, so
    // there is nothing to strip.
    const params = new URLSearchParams(window.location.search);
    params.set('affiliateCode', code);
    window.history.replaceState(
      window.history.state,
      '',
      `/${code}?${params.toString()}`,
    );
  }, []);

  return (
    <>
      <InviteHero {...data.data} />
      <ProductSection {...data.data} />
    </>
  );
}

function AffiliateWork(props: {
  data: PartnerElementData;
  emailLinkAuthActive?: boolean;
  ultraworkSessionToken?: string | null;
  reverifyEmail?: string | null;
  reverifyReason?: string;
  onSessionExpired: () => void;
}) {
  const [isEmailValid, setEmailValid] = useState(
    props.emailLinkAuthActive ?? false,
  );

  return (
    <SessionProvider>
      <main style={{ background: '#000000' }}>
        <Hero
          {...props.data}
          setEmailValid={setEmailValid}
          emailLinkAuthActive={props.emailLinkAuthActive}
          reverifyEmail={props.reverifyEmail}
          reverifyReason={props.reverifyReason}
        />

        <ProductCardSection
          {...props.data}
          isEmailValid={isEmailValid}
          isWorkAffiliate={true}
          emailLinkAuthActive={props.emailLinkAuthActive}
          ultraworkSessionToken={props.ultraworkSessionToken}
          onSessionExpired={props.onSessionExpired}
        />
      </main>
    </SessionProvider>
  );
}

export default function Invite({
  type,
  data,
}: {
  type?: InviteAffiliateType;

  data?: any;
}) {
  const searchParams = useSearchParams();
  const [emailLinkAuthActive, setEmailLinkAuthActive] = useState(false);
  const [ultraworkSessionToken, setUltraworkSessionToken] = useState<
    string | null
  >(null);
  // The last email we know is real (URL, once verify has confirmed it).
  // Kept so we can re-prompt without asking the member to retype it.
  const [knownEmail, setKnownEmail] = useState<string | null>(null);
  // Set to re-open the sign-in modal automatically -- either verify failed
  // on load (a refreshed page reused an already-spent magic-link token) or
  // a later fetch reported the session expired mid-visit.
  const [reverifyEmail, setReverifyEmail] = useState<string | null>(null);
  const [reverifyReason, setReverifyReason] = useState<string>('');

  useEffect(() => {
    if (type !== 'work' || !data?.code) return;

    const email = searchParams?.get('email');
    if (email) setKnownEmail(decodeURIComponent(email));

    // A refresh keeps the session for this company. The magic-link token in
    // the URL is single-use, so verifying it again would always fail.
    const stored = readUltraworkSession();
    if (stored && stored.companyCode === data.code) {
      setEmailLinkAuthActive(true);
      setUltraworkSessionToken(stored.token);
      return;
    }

    const token = searchParams?.get('token');
    if (!email || !token) return;

    const d_email = decodeURIComponent(email);
    const d_token = decodeURIComponent(token);

    verifyOnce(d_token, d_email, data.code).then((r) => {
      if (r.status === 'ok' && r.data?.email === d_email && r.data.sessionToken) {
        writeUltraworkSession({
          token: r.data.sessionToken,
          companyCode: data.code,
        });
        setEmailLinkAuthActive(true);
        setUltraworkSessionToken(r.data.sessionToken);
      } else {
        // The link was already used, or it expired. Open the sign-in modal
        // prefilled -- the member sends the new link themselves.
        setReverifyReason(
          'This link was already used or has expired. Send yourself a new one.',
        );
        setReverifyEmail(d_email);
      }
    });
  }, []);

  const handleSessionExpired = () => {
    clearUltraworkSession();
    setUltraworkSessionToken(null);
    setEmailLinkAuthActive(false);
    if (!knownEmail) return;
    setReverifyReason(
      'Your session expired. Send yourself a new sign-in link.',
    );
    // New value each call so Hero's effect (keyed on reverifyEmail) reopens
    // the modal even when the email is unchanged.
    setReverifyEmail(null);
    setTimeout(() => setReverifyEmail(knownEmail), 0);
  };

  if (type === 'work') {
    return (
      <AffiliateWork
        data={data}
        emailLinkAuthActive={emailLinkAuthActive}
        ultraworkSessionToken={ultraworkSessionToken}
        reverifyEmail={reverifyEmail}
        reverifyReason={reverifyReason}
        onSessionExpired={handleSessionExpired}
      />
    );
  } else {
    return <Affiliate data={data} />;
  }
}
